import { useState, useRef, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mentorConsoleApi } from '../services/api';
import {
  CLIENT_KEY_STORAGE,
  MENTEE_ENTRY_GATE_STORAGE,
  MENTEE_KEY_STORAGE,
  MENTOR_KEY_STORAGE,
  PRO01_ROUTES,
  REGISTRATION_PATHWAY,
  REGISTRATION_PATHWAY_STORAGE,
  SCREENS,
  isMentorActivationKey,
  isOrgMenteeKey,
  isPaidOrgKey,
  isPortalEntranceKey,
  isPro01PortalLoginRoute,
  isProcurementDemoKey,
  normalizeClientKey,
} from '../lib/screenRegistry';
import { resolveIssuedPortalDestination } from '../lib/issuedAccessActivation';

import './PortalLogin.css';



/** Screen-PRO01 — Registration Key Entrance (`/portal-login` only). */
export default function PortalLogin() {
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const submittedRef = useRef(false);

  if (!isPro01PortalLoginRoute(location.pathname)) {
    return null;
  }



  const handleSubmit = async (e: React.FormEvent) => {

    e.preventDefault();

    if (loading || submittedRef.current) return;



    const registrationKey = normalizeClientKey(key.trim());

    if (!registrationKey) {

      setError('Please enter your registration key.');

      return;

    }



    submittedRef.current = true;

    setLoading(true);

    setError('');



    try {

      if (isMentorActivationKey(registrationKey)) {

        const result = await mentorConsoleApi.login(registrationKey);
        const mentor = result?.mentor;
        sessionStorage.setItem(MENTOR_KEY_STORAGE, registrationKey);
        sessionStorage.removeItem(MENTEE_KEY_STORAGE);
        navigate(resolveIssuedPortalDestination({
          isMentor: true,
          agreementAccepted: Boolean(mentor?.serviceAgreementAccepted || mentor?.agreementAccepted),
          introCompleted: Boolean(mentor?.introCompleted),
        }) || '/mentor-console', { replace: true });

        return;

      }



      sessionStorage.removeItem(MENTOR_KEY_STORAGE);

      const result = await login(key.trim());
      const loggedIn = result?.user;

      if (result?.masterTestingOverride || loggedIn?.masterTestingOverride) {
        navigate('/master-admin', { replace: true });
        return;
      }

      if (!isPortalEntranceKey(registrationKey)) {
        setError('Portal access requires a demo access key (PROXXX/ELTXXX/ELTDXXX), paid code (PRO-XXXX / ELT-XXXX), org mentee key (ELT-XXXX), or human mentor key (ELTM-XXXX).');
        submittedRef.current = false;
        setLoading(false);
        return;
      }

      if (isProcurementDemoKey(registrationKey) || loggedIn?.role === 'ORG' || isPaidOrgKey(registrationKey)) {
        sessionStorage.setItem(CLIENT_KEY_STORAGE, registrationKey);
        sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.ORGANISATION);
        sessionStorage.removeItem(MENTEE_KEY_STORAGE);
      } else {
        sessionStorage.setItem(MENTEE_KEY_STORAGE, registrationKey);
      }

      sessionStorage.setItem(MENTEE_ENTRY_GATE_STORAGE, 'portal');

      if (loggedIn?.role === 'ORG') {
        if (loggedIn.introCompleted || loggedIn.accountState !== 'INITIALIZING') {
          navigate('/org-admin', { replace: true });
          return;
        }
        navigate('/introduction', { replace: true });
        return;
      }

      if (isOrgMenteeKey(registrationKey)) {
        const issuedRoute = resolveIssuedPortalDestination({
          isMentor: false,
          agreementAccepted: Boolean(loggedIn?.serviceAgreementAccepted),
          introCompleted: Boolean(loggedIn?.introCompleted),
        });
        if (issuedRoute) {
          navigate(issuedRoute, { replace: true });
          return;
        }
      }

      navigate('/introduction', { replace: true });

    } catch (err: unknown) {

      submittedRef.current = false;

      setError((err as { error?: string }).error || 'Authentication failed');

      setLoading(false);

    }

  };



  const handleKeyChange = (value: string) => {

    setKey(value);

    if (error) setError('');

    submittedRef.current = false;

  };



  const portalKeyValid = useMemo(
    () => Boolean(key.trim()),
    [key],
  );



  return (

    <div
      className="portal-login-screen"
      data-screen-id={SCREENS.PRO01}
      data-screen-surface="portal-login"
      data-screen-route={PRO01_ROUTES.PORTAL_LOGIN}
    >

      <div className="portal-login-logo-wrap">

        <img

          src={encodeURI('/Assets/logo/Mentology Logo.png')}

          alt="Mentology"

          className="portal-login-logo brand-logo"

          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}

        />

      </div>

      <div className="portal-login-content">

        <h1 className="section-header portal-login-heading">Registration Key Entrance</h1>

        <form onSubmit={handleSubmit} className="portal-login-form" noValidate>

          <input

            className="input-field portal-login-input"

            type="text"

            name="registrationKey"

            autoComplete="off"

            spellCheck={false}

            placeholder="Enter access key (PROXXX / ELTXXX / ELT-####)"

            value={key}

            onChange={(e) => handleKeyChange(e.target.value)}

            aria-label="Registration key"

          />

          {error && <p className="error-text portal-login-error">{error}</p>}

          <div className="button-control-group portal-login-actions">

            <button type="submit" className="btn-primary" disabled={loading || !portalKeyValid}>

              {loading ? 'Unlocking...' : 'UNLOCK PORTAL'}

            </button>

            <Link to="/" className="btn-primary">Back to Home</Link>

          </div>

        </form>

      </div>

    </div>

  );

}


